import { Drawer, Space, Table, Input, Radio, Checkbox } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../common/Button/Button";
import { fetchEcomShipping, saveEcomShipping, MODULE_KEY } from "./ecomOrders-actions";
import { ACTION } from "../../util/utils";

interface ShippingConfigProps {
  visible: boolean;
  onClose: () => void;
}

interface ShippingConfigItem {
  Id: number;
  Name: string;
  TrackingURL: string;
  IsDefaultEcomShipping: boolean;
}

function ShippingConfig({ visible, onClose }: ShippingConfigProps) {
  const dispatch = useDispatch();
  const [editedData, setEditedData] = useState<ShippingConfigItem[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const { shippingConfig } = useSelector(
    (state: any) => state[`${MODULE_KEY}${ACTION.STATE_KEY}`]
  );

  useEffect(() => {
    if (visible) {
      dispatch(fetchEcomShipping());
    }
  }, [visible, dispatch]);

  useEffect(() => {
    if (shippingConfig?.data) {
      setEditedData([...shippingConfig.data]);
    }
  }, [shippingConfig?.data]);

  const handleCheckboxChange = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedRows((prev) => [...prev, id]);
    } else {
      setSelectedRows((prev) => prev.filter((rowId) => rowId !== id));
    }
  };

  const handleTrackingUrlChange = (id: number, value: string) => {
    setEditedData((prev) =>
      prev.map((item) => (item.Id === id ? { ...item, TrackingURL: value } : item))
    );
  };

  const handleDefaultChange = (id: number) => {
    setEditedData((prev) =>
      prev.map((item) => ({ ...item, IsDefaultEcomShipping: item.Id === id }))
    );
  };

  const handleSave = () => {
    const checkedData = editedData.filter((item) => selectedRows.includes(item.Id));
    dispatch(saveEcomShipping(checkedData));
    onClose();
  };

  const handleCancel = () => {
    setEditedData([...shippingConfig.data]);
    setSelectedRows([]);
    onClose();
  };

  const columns = [
    {
      title: "",
      key: "checkbox",
      width: 50,
      render: (_: any, record: ShippingConfigItem) => (
        <Checkbox
          checked={selectedRows.includes(record.Id)}
          onChange={(e) => handleCheckboxChange(record.Id, e.target.checked)}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "Name",
      key: "Name",
      width: 150,
    },
    {
      title: "Tracking URL",
      dataIndex: "TrackingURL",
      key: "TrackingURL",
      render: (text: string, record: ShippingConfigItem) => {
        const isChecked = selectedRows.includes(record.Id);
        return (
          <Input
            value={editedData.find((item) => item.Id === record.Id)?.TrackingURL || text}
            onChange={(e) => handleTrackingUrlChange(record.Id, e.target.value)}
            placeholder="Enter tracking URL"
            size="small"
            disabled={!isChecked}
          />
        );
      },
    },
    {
      title: "Default",
      dataIndex: "IsDefaultEcomShipping",
      key: "IsDefaultEcomShipping",
      width: 100,
      align: "center" as const,
      render: (_: any, record: ShippingConfigItem) => {
        const isChecked = selectedRows.includes(record.Id);
        return (
          <Radio
            checked={editedData.find((item) => item.Id === record.Id)?.IsDefaultEcomShipping || false}
            onChange={() => handleDefaultChange(record.Id)}
            disabled={!isChecked}
          />
        );
      },
    },
  ];

  return (
    <Drawer
      title="Shipping Configuration"
      placement="right"
      onClose={handleCancel}
      open={visible}
      width={800}
      footer={
        <Space style={{ float: "right" }}>
          <Button onClick={handleCancel} size="middle">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            size="middle"
            disabled={shippingConfig?.loading || selectedRows.length === 0}
          >
            Save
          </Button>
        </Space>
      }
    >
      <Table
        columns={columns}
        dataSource={editedData}
        rowKey="Id"
        loading={shippingConfig?.loading}
        pagination={false}
        size="small"
        bordered
      />
    </Drawer>
  );
}

export default ShippingConfig;
